import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { SetAvailabilityInput } from '@nest-recruit/shared';
import { AvailabilityEditor, type DeclaredAvailability } from '../../../components/AvailabilityEditor';
import { SkeletonLines } from '../../../components/Skeleton';
import { ServerErrorState } from '../../../components/system-states/SystemState';
import { apiGet, apiPut, type ApiError } from '../../../lib/api-client';
import { useRequiredSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { InterviewsHeader } from '../components/InterviewsHeader';
import styles from '../components/Interviews.module.css';

/**
 * F2.12 for everyone (S19): a panelist sets their own hours and time away here — the
 * HR Lead's USER-02 is not a screen they can open. The scheduler reads exactly this.
 */
export function MyAvailabilityPage(): ReactNode {
  const session = useRequiredSession();
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const key = ['availability', session.id] as const;
  const query = useQuery<readonly DeclaredAvailability[], ApiError>({
    queryKey: key,
    queryFn: () => apiGet<readonly DeclaredAvailability[]>(`/users/${session.id}/availability`),
  });
  const save = useMutation<readonly DeclaredAvailability[], ApiError, SetAvailabilityInput>({
    mutationFn: (input) => apiPut<readonly DeclaredAvailability[]>(`/users/${session.id}/availability`, input),
    onSuccess: (rows) => {
      queryClient.setQueryData(key, rows);
      show({ tone: 'success', message: 'Availability saved', detail: 'New interview times follow it from now on.' });
    },
    onError: (error) => {
      show({ tone: 'danger', message: "Availability wasn't saved", detail: error.message });
      // Changed elsewhere since this was opened: the latest replaces the edit (§5a rule 1).
      if (error.status === 409) void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  return (
    <div className={styles.screen}>
      <InterviewsHeader current="availability" />
      {query.isPending ? (
        <SkeletonLines count={6} />
      ) : query.error ? (
        <ServerErrorState onRetry={() => void query.refetch()} />
      ) : (
        <AvailabilityEditor
          // New rows (a save, or someone else's) start the editor afresh from them.
          key={query.data.map((row) => row.id ?? '').join()}
          title="My availability"
          rows={query.data}
          canEdit
          timeZone={session.timezone}
          isSaving={save.isPending}
          // A save replaces every row, so it names the rows it started from: a save made
          // elsewhere since is refused (409) rather than silently overwritten (§5a rule 1).
          onSave={(input) => save.mutate({ ...input, baseIds: query.data.flatMap((row) => (row.id ? [row.id] : [])) })}
        />
      )}
      <p className={styles.hint}>Times are in your own timezone ({session.timezone}). Interviews already booked in the system count as busy on their own.</p>
    </div>
  );
}
